/** @file ChordCapture: a field that records the next key chord (by event.code) for a shortcut override. */
import { useState } from 'react';
import type { KeyboardEvent, ReactElement } from 'react';
import { chordFromEvent } from '@zilo/pose3d-shortcuts/chordFromEvent';
import { PLATFORM } from '../shell/platform';
import { Button } from '../ui-kit/Button';

/** `onChord` gets the captured chord; `label` names the binding for screen readers. */
export type ChordCaptureProps = { label: string; onChord: (chord: string) => void };

/**
 * "Change" turns into a focused input; the next non-modifier press becomes the chord (Escape alone cancels). The input
 * is a typing target, so the shell's global listener ignores the press while capturing.
 */
export function ChordCapture({ label, onChord }: ChordCaptureProps): ReactElement {
  const [capturing, setCapturing] = useState(false);
  if (!capturing) return <Button variant="ghost" onClick={() => { setCapturing(true); }} aria-label={`Change ${label}`}>Change</Button>;
  const onKey = (e: KeyboardEvent<HTMLInputElement>): void => {
    e.preventDefault();
    const n = e.nativeEvent;
    const chord = chordFromEvent({ code: n.code, ctrlKey: n.ctrlKey, metaKey: n.metaKey, shiftKey: n.shiftKey, altKey: n.altKey, isComposing: n.isComposing }, PLATFORM);
    if (chord === null) return;
    setCapturing(false);
    if (chord !== 'Escape') onChord(chord);
  };
  return (
    <input className="p3-input p3-mono" aria-label={`Press the new keys for ${label}`} placeholder="Press keys..." autoFocus readOnly
      onKeyDown={onKey} onBlur={() => { setCapturing(false); }} />
  );
}
